import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["baseUrl", "crossFlowEnabled", "intl", "isCompassActivated", "isUserAnonymous", "postQueryItemSupplier", "preQueryItemSupplier", "sourceContext", "upsellCtaButtonProps", "userTeamConfig"];
import React, { useCallback, useMemo } from 'react';
import { injectIntl } from 'react-intl-next';
import { SearchFooter } from '@atlassian/search-dialog';
import { AdvancedSearch } from '../../advanced-search/advanced-search';
import { SearchDialogProduct, useProductContext } from '../../product-router';
import { ProductStates } from '../../product-state-machine/product-state-machine-types';
import { messages } from './messages';
import { getSections } from './sections';
import { COMPASS_PRODUCT_ID, useCompassSuppliers } from './suppliers';
import { CompassScope } from './types';
import { compassURLGenerators } from './url-generators';
import { useUserContext } from '../../../common/user-context';
import { useShowAtlassianHomeSearchLink } from '../../../common/show-atlassian-home-search-link';
import { AtlassianHomeSearchLink, getAtlassianHomeSearchUrl } from '../../advanced-search-footer/atlassian-home-search-link';
import { useTypedAggregatorClient } from '../../aggregator-client-context';
var UpsellModal = /*#__PURE__*/React.lazy(function () {
  return import( /* webpackChunkName: "@atlaskit-internal_product-search-dialog/async-chunk/compass-upsell-modal" */'./upsell-tab/upsell-modal').then(function (mod) {
    return {
      default: mod.UpsellModal
    };
  });
});

/**
 * Callback function to be used by PSD's permission supplier.
 * @returns all Compass scope required for Compass tab to function properly.
 */
var upsellPermissionSupplier = function upsellPermissionSupplier() {
  var response = Object.values(CompassScope);
  return Promise.resolve(response);
};
var CompassTabWithIntl = function CompassTabWithIntl(_ref) {
  var baseUrl = _ref.baseUrl,
    crossFlowEnabled = _ref.crossFlowEnabled,
    intl = _ref.intl,
    _ref$isCompassActivat = _ref.isCompassActivated,
    isCompassActivated = _ref$isCompassActivat === void 0 ? false : _ref$isCompassActivat,
    _ref$isUserAnonymous = _ref.isUserAnonymous,
    isUserAnonymous = _ref$isUserAnonymous === void 0 ? false : _ref$isUserAnonymous,
    postQueryItemSupplier = _ref.postQueryItemSupplier,
    preQueryItemSupplier = _ref.preQueryItemSupplier,
    sourceContext = _ref.sourceContext,
    upsellCtaButtonProps = _ref.upsellCtaButtonProps,
    userTeamConfig = _ref.userTeamConfig,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useMemo = useMemo(function () {
      return compassURLGenerators(baseUrl);
    }, [baseUrl]),
    advancedSearchLinkGenerator = _useMemo.advancedSearchLinkGenerator;
  var sections = getSections(intl);
  var _useCompassSuppliers = useCompassSuppliers({
      userTeamConfig: userTeamConfig,
      preQueryItemSupplier: preQueryItemSupplier,
      postQueryItemSupplier: postQueryItemSupplier
    }, sections),
    preQueryItemSupplierWithPeopleAndTeams = _useCompassSuppliers.preQueryItemSupplier,
    postQueryItemSupplierWithPeopleAndTeams = _useCompassSuppliers.postQueryItemSupplier,
    preQuerySectionTitleGenerator = _useCompassSuppliers.preQuerySectionTitleGenerator;
  var getAdvancedSearchButtonLabel = useCallback(function (productState) {
    switch (productState) {
      case ProductStates.PreQueryLoading:
      case ProductStates.PreQuerySuccess:
      case ProductStates.PreQueryError:
      case ProductStates.PreQueryNoResult:
        return intl.formatMessage(messages.compass_view_all_components);
      default:
        return intl.formatMessage(messages.compass_view_all_results);
    }
  }, [intl]);
  var shouldUpsell = !isUserAnonymous && crossFlowEnabled && !isCompassActivated;
  var permissionSupplier = shouldUpsell ? upsellPermissionSupplier : rest.permissionSupplier;
  var _useUserContext = useUserContext(),
    orgId = _useUserContext.orgId;
  var _useProductContext = useProductContext(),
    getPrimaryProduct = _useProductContext.getPrimaryProduct;
  var primaryProduct = getPrimaryProduct();
  var aggregatorClient = useTypedAggregatorClient();
  var cloudId = aggregatorClient === null || aggregatorClient === void 0 ? void 0 : aggregatorClient.getAggregatorConfig().cloudId;
  var showAtlassianHomeSearchLink = !!(useShowAtlassianHomeSearchLink(primaryProduct === null || primaryProduct === void 0 ? void 0 : primaryProduct.id) && orgId && cloudId);
  var generateAdvancedSearchUrl = useCallback(function (query, _, usage) {
    if (usage === 'onInputEnterModified' && showAtlassianHomeSearchLink) {
      return getAtlassianHomeSearchUrl(orgId, cloudId, query, primaryProduct === null || primaryProduct === void 0 ? void 0 : primaryProduct.id);
    }
    return advancedSearchLinkGenerator(query);
  }, [advancedSearchLinkGenerator, orgId, cloudId, showAtlassianHomeSearchLink, primaryProduct === null || primaryProduct === void 0 ? void 0 : primaryProduct.id]);
  return /*#__PURE__*/React.createElement(SearchDialogProduct, _extends({
    id: COMPASS_PRODUCT_ID,
    sections: sections,
    title: intl.formatMessage(messages.compass_tab_label),
    expandedStateInputPlaceholder: intl.formatMessage(messages.compass_expanded_input_placeholder),
    generateAdvancedSearchUrl: generateAdvancedSearchUrl,
    preQuerySectionTitleGenerator: preQuerySectionTitleGenerator,
    preQueryItemSupplier: preQueryItemSupplierWithPeopleAndTeams,
    postQueryItemSupplier: postQueryItemSupplierWithPeopleAndTeams,
    permissionSupplier: permissionSupplier
  }, rest), function (_ref2) {
    var linkComponent = _ref2.linkComponent,
      productState = _ref2.productState,
      query = _ref2.query;
    if (shouldUpsell) {
      return {
        Body: function Body() {
          return /*#__PURE__*/React.createElement(UpsellModal, {
            sourceContext: sourceContext !== null && sourceContext !== void 0 ? sourceContext : 'product search dialog',
            ctaButtonProps: upsellCtaButtonProps
          });
        },
        Footer: function Footer() {
          return /*#__PURE__*/React.createElement(React.Fragment, null);
        }
      };
    }
    var advancedSearchUrl = advancedSearchLinkGenerator(query);
    var message = getAdvancedSearchButtonLabel(productState);
    return {
      Header: function Header() {
        return /*#__PURE__*/React.createElement(AdvancedSearch, {
          advancedSearchMessage: message,
          advancedSearchUrl: advancedSearchUrl,
          linkComponent: linkComponent
        });
      },
      Footer: function Footer() {
        return /*#__PURE__*/React.createElement(React.Fragment, null, showAtlassianHomeSearchLink && /*#__PURE__*/React.createElement(SearchFooter, null, /*#__PURE__*/React.createElement(AtlassianHomeSearchLink, {
          orgId: orgId,
          cloudId: cloudId,
          showModifierIcon: true
        })));
      }
    };
  });
};
export var CompassTab = injectIntl(CompassTabWithIntl);